import { PlusOutlined } from '@ant-design/icons';
import { PageController } from '@client/components/_core/layout/PageController';
import { TableToolbar } from '@client/components/_core/table/TableToolbar';
import { OrgSelector } from '@client/components/_shared/selectors/common/OrgSelector';
import { Button } from '@client/components/_ui/data-entry/ButtonModern';
import { errorMessage } from '@client/components/_ui/feedback/error-message/ErrorMessage';
import { message } from '@client/components/_ui/feedback/message';
import {
    type ControlLibraryItemAction,
    ControlLibraryItemEdit,
} from '@client/components/controls/ControlLibraryItemEdit';
import { useTrpc } from '@client/hooks/api/useTrpc';
import { useNavigate } from '@client/hooks/navigation/useNavigate';
import { usePathState } from '@client/hooks/navigation/usePathState';
import { createColumnHelper, PrismaTable, type PrismaTableColumn, usePrismaTable } from '@client/hooks/table-v2';
import type { RouterInputs, RouterOutputs } from '@client/trpc/client';
import { DEFAULT_MAX_ACCESSIBLE_RECORDS, DEFAULT_PAGE_SIZE } from '@shared/constants';
import { createFileRoute } from '@tanstack/react-router';
import { Form, Modal, Space, Tooltip, Typography } from 'antd';
import { Copy } from 'lucide-react';
import { useMemo, useState } from 'react';
import { ControlLibraryFilters } from './ControlLibraryFilters';

export const Route = createFileRoute('/admin-console/platform-content/obligations/controls-library/')({
    staticData: {
        ui: {
            title: 'Controls library',
            subtitle: 'Manage your controls library.',
            widthVariant: 'fullWidth',
            menuDescription: 'Master controls library',
        },
    },
    component: ControlLibrary,
});

type ControlLibraryItem = RouterOutputs['admin']['controlLibrary']['listControlLibraryItems']['rows'][number];
type QueryVariables = RouterInputs['admin']['controlLibrary']['listControlLibraryItems'];
type OrderBy = NonNullable<QueryVariables['orderBy']>;

type SendToOrgForm = {
    orgIds: bigint[];
};

function useControlLibraryColumns(
    navigate: ReturnType<typeof useNavigate>,
    onCopy: (record: ControlLibraryItem) => Promise<void>,
    onDelete: (id: bigint) => Promise<void>,
    createControlLibraryItemLoading: boolean,
): readonly PrismaTableColumn<ControlLibraryItem, OrderBy, any>[] {
    const columnHelper = createColumnHelper<ControlLibraryItem, OrderBy>();

    return useMemo(
        () => [
            columnHelper.accessor('id', {
                id: 'id',
                header: 'Control No.',
                cell: ({ value }) => String(value),
            }),
            columnHelper.accessor('name', {
                id: 'name',
                header: 'Name',
            }),
            columnHelper.accessor('objective', {
                id: 'objective',
                header: 'Objective',
                cell: ({ value }) => (
                    <Tooltip placement="bottom" title={value}>
                        <Typography.Paragraph ellipsis style={{ maxWidth: 250, minWidth: 100, marginBottom: 0 }}>
                            {value}
                        </Typography.Paragraph>
                    </Tooltip>
                ),
            }),
            columnHelper.accessor('description', {
                id: 'description',
                header: 'Description',
                cell: ({ value }) => (
                    <Tooltip placement="bottom" title={value}>
                        <Typography.Paragraph ellipsis style={{ maxWidth: 250, minWidth: 100, marginBottom: 0 }}>
                            {value}
                        </Typography.Paragraph>
                    </Tooltip>
                ),
            }),
            columnHelper.accessor('owner', {
                id: 'owner',
                header: 'Owner',
            }),
            columnHelper.accessor('AssignedTo', {
                id: 'AssignedTo',
                header: 'Assigned to',
                cell: ({ value }) => value?.map((org: { name: string }) => org.name).join(', '),
            }),
            columnHelper.actions({
                cell: ({ row }) => (
                    <Space>
                        <Button.Icon
                            icon={<Copy />}
                            color="blue"
                            size="small"
                            tooltip="Create a copy"
                            loading={createControlLibraryItemLoading}
                            onClick={(e) => {
                                e.stopPropagation();
                                void onCopy(row);
                            }}
                        />
                        <Button.Delete
                            onConfirm={(onSuccess) => {
                                void onDelete(row.id).then(onSuccess);
                            }}
                        />
                    </Space>
                ),
            }),
        ],
        [navigate, onCopy, onDelete, createControlLibraryItemLoading],
    );
}

function ControlLibrary() {
    const navigate = useNavigate();
    const { trpc } = useTrpc();

    const [whereConditions, setWhereConditions] = useState<QueryVariables['where']>({});
    const [queryParams, setQueryParams] = usePathState<QueryVariables>('queryVariables', {
        limit: DEFAULT_PAGE_SIZE,
        orderBy: [],
    });

    const [controlLibraryItemAction, setControlLibraryItemAction] = useState<ControlLibraryItemAction>(['none']);
    const [isModalVisible, setIsModalVisible] = useState(false);
    const [selectedRowKeys, setSelectedRowKeys] = useState<bigint[]>([]);
    const [selectedRows, setSelectedRows] = useState<ControlLibraryItem[]>([]);

    const { data, isFetching, refetch } = trpc.admin.controlLibrary.listControlLibraryItems.useQuery({
        ...queryParams,
        where: whereConditions,
    });
    const { mutateAsync: createControlLibraryItem, isPending: createControlLibraryItemLoading } =
        trpc.admin.controlLibrary.createControlLibraryItem.useMutation();
    const { mutateAsync: deleteControlLibraryItem } = trpc.admin.controlLibrary.deleteControlLibraryItem.useMutation();
    const { mutateAsync: createManyControls, isPending: createManyControlsLoading } =
        trpc.admin.control.createManyControls.useMutation();

    const [sendToOrgForm] = Form.useForm<SendToOrgForm>();

    const onCopy = async (record: ControlLibraryItem) => {
        try {
            message.loading('Copying...');

            const { description, name, objective, owner } = record;
            const control = await createControlLibraryItem({
                objective,
                description,
                owner: owner || '',
                name: name || '',
            });

            message.success(`Control copied successfully. Copy Control No: ${control?.id}`);

            void refetch();
        } catch (e) {
            errorMessage.show(e);
        }
    };

    const onDelete = async (id: bigint) => {
        try {
            message.loading('Deleting...');
            await deleteControlLibraryItem({
                id,
            });
            message.success('Deleted.');
            void refetch();
        } catch (e) {
            errorMessage.show(e);
        }
    };

    const columns = useControlLibraryColumns(navigate, onCopy, onDelete, createControlLibraryItemLoading);

    const tableProps = usePrismaTable({
        data: data?.rows || [],
        total: data?.total || 0,
        loading: isFetching,
        columns,
        rowKey: 'id',
        queryParams: queryParams,
        onParamsChange: setQueryParams,
        pagination: {
            position: 'both',
            showSizeChanger: true,
            maxAccessibleRecords: DEFAULT_MAX_ACCESSIBLE_RECORDS,
        },
        columnVisibility: {
            enabled: true,
            persistKey: 'control-library-table-columns',
            enableReordering: true,
        },
        refreshable: {
            enabled: true,
            onRefresh: () => refetch(),
        },
        rowSelection: {
            enabled: true,
            type: 'checkbox',
            preserveSelectedRowKeys: true,
            selectedRowKeys,
            onChange: (keys, rows) => {
                setSelectedRowKeys(keys as bigint[]);
                setSelectedRows(rows);
            },
        },
    });

    const onSendToOrganizations = async (values: SendToOrgForm) => {
        const { orgIds } = values;
        const controlsToInsert = selectedRows.flatMap(({ id, objective, description, name, owner }) =>
            orgIds.map((orgId) => ({
                objective,
                description,
                name: name || '',
                ownerHint: owner || '',
                status: 'DRAFT',
                libraryItemId: id,
                orgId,
            })),
        );

        try {
            message.loading('Sending to organizations...');
            await createManyControls(controlsToInsert);
            message.success('Successfully sent to organizations.');
            setIsModalVisible(false);
            sendToOrgForm.resetFields();
            void refetch();
            setSelectedRowKeys([]);
            setSelectedRows([]);
        } catch (e) {
            errorMessage.show(e);
        }
    };

    return (
        <PageController route={Route}>
            <ControlLibraryItemEdit
                action={controlLibraryItemAction}
                onClose={() => {
                    setControlLibraryItemAction(['none']);
                    void refetch();
                }}
            />

            <Modal
                confirmLoading={createManyControlsLoading}
                open={isModalVisible}
                mask={{ closable: false }}
                title="Assign controls to register"
                onCancel={() => {
                    setIsModalVisible(false);
                    sendToOrgForm.resetFields();
                }}
                okText="Submit"
                okButtonProps={{
                    autoFocus: true,
                    htmlType: 'submit',
                    form: 'sendToOrgForm',
                }}
            >
                <Form form={sendToOrgForm} layout="vertical" id="sendToOrgForm" onFinish={onSendToOrganizations}>
                    <Form.Item label="Select the organization(s)" name="orgIds">
                        <OrgSelector mode="multiple" />
                    </Form.Item>
                </Form>
            </Modal>

            <TableToolbar
                filters={
                    <ControlLibraryFilters
                        onChange={(where) => {
                            setWhereConditions(where);
                            setQueryParams((prev) => ({ ...prev, offset: 0 }));
                        }}
                    />
                }
                variant="enhanced"
                actions={
                    <Space>
                        <Button
                            type="secondary"
                            onClick={() => setIsModalVisible(true)}
                            disabled={!(selectedRowKeys.length > 0)}
                        >
                            Send to organisation
                        </Button>
                        <Button
                            type="primary"
                            icon={<PlusOutlined />}
                            onClick={() => {
                                setControlLibraryItemAction(['add']);
                            }}
                        >
                            Add control
                        </Button>
                    </Space>
                }
            />

            <PrismaTable
                {...tableProps}
                size="small"
                scroll={{ x: 'max-content' }}
                onRow={(row) => ({
                    className: 'cursor-pointer',
                    onClick: (e) => {
                        navigate(
                            {
                                to: '/admin-console/platform-content/obligations/controls-library/$id/details',
                                params: {
                                    id: row.id,
                                },
                            },
                            e,
                        );
                    },
                })}
            />
        </PageController>
    );
}
